import React, { useState, useEffect, useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import {
  ClockIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  PhoneIcon,
  XMarkIcon,
  SparklesIcon,
  BellAlertIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

interface Announcement {
  _id?: string;
  id: number;
  icon: React.ReactNode;
  message: string;
  type: 'info' | 'warning' | 'error' | 'success';
  priority: 'low' | 'medium' | 'high';
  isActive: boolean;
  flyer?: {
    url?: string;
  };
  createdAt?: string;
  updatedAt?: string;
}

interface FeatureFlags {
  directPurchase: boolean;
  manualTopup: boolean;
  enableDeposit: boolean;
  enablePurchase: boolean;
}

interface NetworkColors {
  gradient: string;
  solid: string;
  light: string;
  dark: string;
  glow: string;
}

// Default announcements (will be replaced by API data)
const defaultAnnouncements: Announcement[] = [
  {
    id: 1,
    icon: <ClockIcon className="w-5 h-5" />,
    message: "Hours: Mon-Sat 8:30am-9pm • Sun 2pm-9pm",
    type: 'info',
    priority: 'low',
    isActive: true,
  },
  {
    id: 2,
    icon: <PhoneIcon className="w-5 h-5" />,
    message: "Verify phone & network before ordering",
    type: 'warning',
    priority: 'medium',
    isActive: true,
  },
  {
    id: 3,
    icon: <ExclamationTriangleIcon className="w-5 h-5" />,
    message: "No refund for wrong transactions!",
    type: 'error',
    priority: 'high',
    isActive: true,
  },
  {
    id: 4,
    icon: <ShieldCheckIcon className="w-5 h-5" />,
    message: "Report unreceived orders within 24hrs",
    type: 'info',
    priority: 'medium',
    isActive: true,
  },
];

// Modernized network-based color schemes with refined gradients
const networkColors: Record<string, NetworkColors> = {
  mtn: {
    gradient: 'from-primary-600 via-primary-700 to-primary-800',
    solid: 'bg-primary-600',
    light: 'bg-primary-50 dark:bg-primary-900/10',
    dark: 'bg-primary-950',
    glow: 'shadow-lg shadow-primary-500/20',
  },
  airteltigo: {
    gradient: 'from-primary-600 via-primary-700 to-primary-800',
    solid: 'bg-primary-600',
    light: 'bg-primary-50 dark:bg-primary-900/10',
    dark: 'bg-primary-950',
    glow: 'shadow-lg shadow-primary-500/20',
  },
  telecel: {
    gradient: 'from-primary-600 via-primary-700 to-primary-800',
    solid: 'bg-primary-600',
    light: 'bg-primary-50 dark:bg-primary-900/10',
    dark: 'bg-primary-950',
    glow: 'shadow-lg shadow-primary-500/20',
  },
  default: {
    gradient: 'from-primary-600 via-primary-700 to-primary-800',
    solid: 'bg-gradient-to-r from-primary-600 to-primary-700',
    light: 'bg-primary-50 dark:bg-primary-900/10',
    dark: 'bg-primary-950',
    glow: 'shadow-lg shadow-primary-500/20',
  },
};

const AnnouncementBanner: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const [announcements, setAnnouncements] = useState<Announcement[]>(defaultAnnouncements);
  const [currentNetwork, setCurrentNetwork] = useState<string>('default');
  const [isVisible, setIsVisible] = useState(true);

  const marqueeContainerRef = useRef<HTMLDivElement | null>(null);
  const marqueeTextRef = useRef<HTMLSpanElement | null>(null);
  const [marqueeMetrics, setMarqueeMetrics] = useState<{ distance: number; duration: number } | null>(null);

  // Detect network from URL path
  useEffect(() => {
    const path = location.pathname.toLowerCase();
    if (path.includes('/packages/mtn') || path.includes('mtn')) {
      setCurrentNetwork('mtn');
    } else if (path.includes('/packages/airteltigo') || path.includes('airteltigo')) {
      setCurrentNetwork('airteltigo');
    } else if (path.includes('/packages/telecel') || path.includes('telecel')) {
      setCurrentNetwork('telecel');
    } else {
      setCurrentNetwork('default');
    }
  }, [location.pathname]);

  // Fetch announcements from API
  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        const [announcementsResponse, featuresResponse] = await Promise.allSettled([
          api.get('/announcements/active'),
          api.get('/config/features')
        ]);

        const featureAnnouncements: Announcement[] = [];
        if (featuresResponse.status === 'fulfilled') {
          const featuresData = featuresResponse.value?.data;
          const flags: FeatureFlags | null = featuresData?.success && featuresData?.features ? featuresData.features : null;

          if (flags) {
            const items: Array<{ enabled: boolean; message: string }> = [
              {
                enabled: flags.enablePurchase,
                message: 'Purchases are currently unavailable. Please try again later.'
              },
              {
                enabled: flags.enableDeposit,
                message: 'Deposits are currently unavailable. Please try again later.'
              },
              {
                enabled: flags.directPurchase,
                message: 'Direct purchase is currently unavailable. Please use your wallet balance.'
              },
              {
                enabled: flags.manualTopup,
                message: 'Manual top-up is currently unavailable.'
              }
            ];

            let idSeed = 9000;
            for (const item of items) {
              if (item.enabled) continue;
              featureAnnouncements.push({
                id: idSeed++,
                icon: <InformationCircleIcon className="w-5 h-5" />,
                message: item.message,
                type: 'warning',
                priority: 'high',
                isActive: true,
              });
            }
          }
        }

        if (announcementsResponse.status === 'fulfilled') {
          const response = announcementsResponse.value;
          if (response.data.success && response.data.data.length > 0) {
          // Map API data to component format
          const apiAnnouncements = response.data.data.map((ann: any, index: number) => ({
            _id: ann._id,
            id: index + 1,
            icon: getIconForType(ann.type),
            message: ann.message,
            type: ann.type,
            priority: ann.priority,
            isActive: ann.isActive,
            flyer: ann.flyer,
            createdAt: ann.createdAt,
            updatedAt: ann.updatedAt,
          }));
          const combined = [...featureAnnouncements, ...apiAnnouncements];
          setAnnouncements(combined.length > 0 ? combined : defaultAnnouncements);
          return;
        }

          if (featureAnnouncements.length > 0) {
            setAnnouncements(featureAnnouncements);
            return;
          }

          // No announcements from API
          setAnnouncements(defaultAnnouncements);
          return;
        }

        if (featureAnnouncements.length > 0) {
          setAnnouncements(featureAnnouncements);
          return;
        }
      } catch (error) {

        // Keep using default announcements
      }
    };

    if (isAuthenticated) {
      fetchAnnouncements();
    }
  }, [isAuthenticated]);

  // Check if user dismissed the banner (expires after 24 hours)
  useEffect(() => {
    try {
      const dismissedRaw = sessionStorage.getItem('announcement_dismissed');
      if (dismissedRaw) {
        const dismissedAt = Number(dismissedRaw);
        if (!Number.isNaN(dismissedAt) && Date.now() - dismissedAt < 24 * 60 * 60 * 1000) {
          setDismissed(true);
        } else {
          // Expired — clear it so banner can show again
          sessionStorage.removeItem('announcement_dismissed');
        }
      }
    } catch { /* ignore */ }
  }, []);

  // Helper function to get icon based on type
  const getIconForType = (type: string) => {
    switch (type) {
      case 'warning':
        return <ExclamationTriangleIcon className="w-5 h-5" />;
      case 'error':
        return <BellAlertIcon className="w-5 h-5" />;
      case 'success':
        return <ShieldCheckIcon className="w-5 h-5" />;
      default:
        return <SparklesIcon className="w-5 h-5" />;
    }
  };

  // Auto-rotate announcements with smoother timing
  useEffect(() => {
    if (!isAuthenticated || dismissed || announcements.length <= 1) {
      return;
    }

    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % announcements.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [isAuthenticated, dismissed, announcements.length]);

  const handleDismiss = useCallback(() => {
    setIsVisible(false);
    setTimeout(() => {
      setDismissed(true);
      try {
        sessionStorage.setItem('announcement_dismissed', String(Date.now()));
      } catch { /* ignore */ }
    }, 300);
  }, []);

  const current = announcements[currentIndex];
  const colors = networkColors[currentNetwork];

  const overlayClass = 'bg-black/25';

  const shouldScroll = useMemo(() => {
    return Boolean(current?._id);
  }, [current?._id]);

  useLayoutEffect(() => {
    if (!current) return;

    const container = marqueeContainerRef.current;
    const text = marqueeTextRef.current;
    if (!container || !text) {
      setMarqueeMetrics(null);
      return;
    }

    const compute = () => {
      const containerWidth = container.clientWidth;
      const textWidth = text.scrollWidth;

      if (containerWidth <= 0 || textWidth <= 0) {
        setMarqueeMetrics(null);
        return;
      }

      const shouldAnimate = shouldScroll || textWidth > containerWidth;
      if (!shouldAnimate) {
        setMarqueeMetrics(null);
        return;
      }

      const gapPx = 48;
      const distance = textWidth + gapPx;
      const pxPerSecond = 70;
      const duration = Math.max(6, distance / pxPerSecond);
      setMarqueeMetrics({ distance, duration });
    };

    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, [current, shouldScroll]);

  // Don't render if not authenticated or dismissed
  if (!isAuthenticated || dismissed) {
    return null;
  }

  // Safety check: Don't render if no announcements or current is undefined
  if (!announcements.length || !current) {
    return null;
  }

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          className="w-full overflow-hidden"
        >
          <div className="max-w-7xl mx-auto px-3 sm:px-4 py-2">
            <motion.div
              className={`relative overflow-hidden rounded-2xl ${colors.glow}`}
              whileHover={{ scale: 1.005 }}
              transition={{ duration: 0.2 }}
            >
              {/* Gradient background */}
              <div className={`absolute inset-0 bg-gradient-to-r ${colors.gradient}`} />

              <div className={`absolute inset-0 ${overlayClass}`} />
              
              {/* Subtle pattern overlay */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_50%,rgba(255,255,255,0.3),transparent_70%)]" />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_50%,rgba(0,0,0,0.2),transparent_70%)]" />
              </div>

              {/* Content container */}
              <div className="relative z-10 flex items-center justify-between gap-3 px-3 sm:px-6 py-2.5">
                {/* Status indicator */}
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="flex-shrink-0 flex items-center gap-2 px-2.5 py-1 rounded-full bg-black/20 backdrop-blur-sm border border-white/20"
                  >
                    <motion.div
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ duration: 2, repeat: Infinity }}
                      className="w-2 h-2 rounded-full bg-green-400 shadow-lg shadow-green-400/50"
                    />
                    <span className="text-[11px] font-semibold text-white/90">
                      Live
                    </span>
                  </motion.div>

                  {/* Announcement content */}
                  <div className="flex-1 min-w-0">
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={current.id}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.3 }}
                        className="flex items-center gap-2 sm:gap-3"
                      >
                        {/* Icon */}
                        <div className="flex-shrink-0 hidden sm:block">
                          {React.cloneElement(current.icon as React.ReactElement, {
                            className: 'w-4 h-4 text-white/90',
                          })}
                        </div>

                        {/* Message */}
                        <div ref={marqueeContainerRef} className="flex-1 min-w-0 overflow-hidden">
                          {marqueeMetrics ? (
                            <motion.div
                              className="flex items-center whitespace-nowrap"
                              animate={{ x: [0, -marqueeMetrics.distance] }}
                              transition={{
                                x: {
                                  repeat: Infinity,
                                  repeatType: 'loop',
                                  duration: marqueeMetrics.duration,
                                  ease: 'linear'
                                }
                              }}
                            >
                              <span ref={marqueeTextRef} className="text-sm sm:text-base font-medium text-white drop-shadow-sm">
                                {current.message}
                              </span>
                              {current.flyer?.url && (
                                <a
                                  href={current.flyer.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="ml-4 text-sm sm:text-base font-medium text-white underline decoration-white/70 drop-shadow-sm"
                                >
                                  Flyer
                                </a>
                              )}
                              <span className="px-6 text-white/70">•</span>
                              <span className="text-sm sm:text-base font-medium text-white drop-shadow-sm">{current.message}</span>
                            </motion.div>
                          ) : (
                            <p
                              className="text-sm sm:text-base font-medium text-white whitespace-nowrap truncate drop-shadow-sm"
                            >
                              <span ref={marqueeTextRef}>{current.message}</span>
                              {current.flyer?.url && (
                                <a
                                  href={current.flyer.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="ml-2 underline decoration-white/70"
                                >
                                  Flyer
                                </a>
                              )}
                            </p>
                          )}
                        </div>
                      </motion.div>
                    </AnimatePresence>
                  </div>
                </div>

                {/* Right controls */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {/* Pagination dots */}
                  {announcements.length > 1 && (
                    <div className="hidden sm:flex items-center gap-1.5 px-2">
                      {announcements.map((_, idx) => (
                        <button
                          key={idx}
                          onClick={() => setCurrentIndex(idx)}
                          className={`h-1.5 rounded-full transition-all duration-300 ${
                            idx === currentIndex
                              ? 'w-6 bg-white'
                              : 'w-1.5 bg-white/40 hover:bg-white/60'
                          }`}
                          aria-label={`Go to announcement ${idx + 1}`}
                        />
                      ))}
                    </div>
                  )}

                  {/* Close button */}
                  <motion.button
                    onClick={handleDismiss}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className="flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-white/20 hover:bg-white/30 text-white backdrop-blur-md border border-white/30 transition-all shrink-0 ml-2"
                    aria-label="Dismiss announcement"
                  >
                    <XMarkIcon className="w-5 h-5 text-white" />
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AnnouncementBanner;
